/**
 * Staff sign-in, `/login` (ORDR-63).
 *
 * A thin wrapper over `@pirouz-ca/ui-auth`, modelled on paio-community-ui's
 * `screens/Login.tsx`: the kickoff, loading state and error copy live in the
 * package; this file decides the brand, the providers and the return path.
 *
 * GOOGLE ONLY. It is the provider verified end to end for `app_id=ordering`
 * (authorize returns a Google URL whose `redirect_uri` is this app's callback,
 * and that URI is on the Google client). Offering a provider nobody has
 * configured for this app is a button that fails after the click.
 *
 * EMAILED LINK, ON STAFF HOSTS (ORDR-189). Under the Google button, for staff
 * whose mailbox is not Google. `authConfig` decides whether it is offered: only
 * on a staff host, because paio-auth refuses a link base anywhere else. See
 * `EmailLinkForm.tsx`.
 *
 * Served outside `OrderingContextProvider` (see `Root.tsx`), so an anonymous
 * visitor sees this, never the staff door's notice.
 */

import { useMemo, useState, type ReactElement } from "react";
import { LoginScreen, orderedAuthProviders, useOAuthLogin } from "@pirouz-ca/ui-auth";
import { authConfig, LOGIN_BRAND } from "../auth/config.js";
import { returnPathFromSearch } from "../auth/returnPath.js";
import { EmailLinkForm } from "./EmailLinkForm.js";

const PROVIDERS = orderedAuthProviders().filter((provider) => provider.id === "google");

export interface LoginProps {
  /** Injected for tests. Defaults to the host this document was served on. */
  readonly hostname?: string;
}

export function Login({ hostname = window.location.hostname }: LoginProps): ReactElement {
  // Read once at mount: the address does not change under this screen.
  const [returnUrl] = useState(() => returnPathFromSearch(window.location.search));
  const config = useMemo(() => authConfig(returnUrl, hostname), [returnUrl, hostname]);
  const { signIn, loadingProvider, errorCode } = useOAuthLogin(config);

  return (
    <main className="flex min-h-screen items-center justify-center p-4">
      <LoginScreen
        brand={LOGIN_BRAND}
        providers={PROVIDERS}
        onProviderSelect={signIn}
        loadingProvider={loadingProvider}
        errorCode={errorCode}
        copy={{
          heading: "Sign in to Ordering",
          subtitle: "For the kitchen, staff and admin screens. Diners do not need to sign in.",
        }}
        // Not `config=`: that would draw LoginScreen's own link form as well.
        renderFooter={
          config.emailLink?.enabled === true
            ? () => <EmailLinkForm config={config} returnPath={returnUrl} />
            : undefined
        }
      />
    </main>
  );
}
